// src/pages/install-app/InstallAppPage.tsx
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { isNative } from '../../../../platform';

import { usePwaInstall } from '../../../../hooks/use-pwa-install';
import { useDeviceOverride, InstallAppDevOverlay } from './install-app-dev-overlay';
import { InstallAppIosSection } from './install-app-ios';
import { InstallAppDefaultSection } from './install-app-default';
import { OnboardingStepProps } from '../../registration';
import { useAuth } from '../../../../contexts/auth/auth-context';

/**
 * Cross-context “continue onboarding” must use a cookie.
 */
const PWA_PENDING_NEXT_STEP_COOKIE = 'mailander_pwa_pending_next_step';

/* ───────────────── cookie helpers ───────────────── */

const setCookie = (name: string, value: string, maxAgeSeconds: number) => {
  if (typeof document === 'undefined') return;
  document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(
    value
  )}; Max-Age=${maxAgeSeconds}; Path=/; SameSite=Lax; Secure`;
};

const getCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null;
  const encodedName = encodeURIComponent(name) + '=';
  const parts = document.cookie.split('; ');
  for (const part of parts) {
    if (part.startsWith(encodedName)) {
      return decodeURIComponent(part.substring(encodedName.length));
    }
  }
  return null;
};

const deleteCookie = (name: string) => {
  if (typeof document === 'undefined') return;
  document.cookie = `${encodeURIComponent(name)}=; Max-Age=0; Path=/; SameSite=Lax; Secure`;
};

const setPendingNextStep = () => setCookie(PWA_PENDING_NEXT_STEP_COOKIE, '1', 24 * 60 * 60);
const clearPendingNextStep = () => deleteCookie(PWA_PENDING_NEXT_STEP_COOKIE);
const hasPendingNextStep = () => getCookie(PWA_PENDING_NEXT_STEP_COOKIE) === '1';

export const Install: React.FC<OnboardingStepProps> = ({ goNext }) => {
  // Native app is already installed — skip this page
  useEffect(() => {
    if (isNative()) goNext();
  }, [goNext]);

  if (isNative()) return null;

  const {
    isStandalone,
    canPromptProgrammatically,
    promptInstall,
    manualPlatform,
    isIos,
    isAndroid,
    isDesktop,
    userAgent,
  } = usePwaInstall();

  const {
    deviceOverride,
    setDeviceOverride,
    effectiveIsIos,
    effectiveIsAndroid,
    effectiveIsDesktop,
    isDevEnv,
  } = useDeviceOverride(isIos, isAndroid, isDesktop);

  const isIosMode = effectiveIsIos && !effectiveIsAndroid && !effectiveIsDesktop;

  const { dev } = useAuth();

  const didAutoAdvanceRef = useRef(false);

  const [rehydratedInStandalone, setRehydratedInStandalone] = useState(false);

  /**
   * HARD BLOCK RULE:
   * - If NOT standalone -> user cannot proceed (we keep them on this screen)
   *
   * STANDALONE BEHAVIOR:
   * - Auto-advance immediately when standalone is detected
   */
  useEffect(() => {
    if (!isStandalone) {
      didAutoAdvanceRef.current = false;
      setRehydratedInStandalone(false);
      return;
    }

    if (!didAutoAdvanceRef.current) {
      didAutoAdvanceRef.current = true;
      clearPendingNextStep();
      goNext();
    }
  }, [isStandalone, goNext]);

  const handleInstall = useCallback(async () => {
    if (isStandalone) return;

    // Always set pending flag so standalone can continue after user opens from icon
    setPendingNextStep();

    if (canPromptProgrammatically) {
      const ok = await promptInstall();
      // If dismissed, we still keep pending for a while (harmless)
      // User might install later manually and open standalone.
      void ok;
    } else {
      // manual install (iOS / Firefox etc.)
      // pending cookie already set above
    }
  }, [isStandalone, canPromptProgrammatically, promptInstall]);


  // Standalone but still not advancing (prod) usually means:
  // - pending cookie wasn't set AND cookie handoff didn't exist
  // We'll show a hint.
  const standaloneNoAdvanceHint =
    isStandalone && !isDevEnv && !dev?.isDevEnv && !rehydratedInStandalone && !hasPendingNextStep();

  return (
    <div style={{ height: '100%', maxHeight: '100%', width: '100%', marginTop: '64px'}}>
      {isIosMode ? (
        <InstallAppIosSection />
      ) : (
        <InstallAppDefaultSection
          isInstalled={false}
          isStandalone={isStandalone}
          hasInstalledRecently={false}
          onInstall={handleInstall}
        />
      )}

      {standaloneNoAdvanceHint && (
        <div
          style={{
            marginTop: 16,
            background: '#23252A',
            border: '1px solid #2B2D33',
            borderRadius: 12,
            padding: 16,
          }}
        >
          <div style={{ color: 'white', fontSize: 14, fontWeight: 700 }}>
            Couldn’t detect the “continue” handoff
          </div>
          <div style={{ color: '#B7BDCB', marginTop: 8, lineHeight: 1.45, fontSize: 13 }}>
            Go back to the browser tab where you logged in, refresh once, then open the installed
            app again. (This syncs the session handoff cookie.)
          </div>
        </div>
      )}

      <InstallAppDevOverlay
        isDevEnv={isDevEnv}
        deviceOverride={deviceOverride}
        setDeviceOverride={setDeviceOverride}
        isDesktop={isDesktop}
        isAndroid={isAndroid}
        isIos={isIos}
        manualPlatform={manualPlatform}
        userAgent={userAgent}
      />
    </div>
  );
};

(Install as any).smallLogo = true;
(Install as any).progress = false;
(Install as any).showLogo = true;
(Install as any).containerAlign = 'flex-start';
(Install as any).containerJustify = 'flex-start';
(Install as any).background = 'linear-gradient(180deg, #222327 0%, #1A1A1C 100%);';
